import {
    Card,
    CardContent,
    Box,
    Grid,
    Fade} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { fetchLLMList } from '../../../store/slices/LLMSlice';
import { setSelectedAgent } from '../../../store/slices/SelectedAgentSlice';
import CustomDialog from '../../../components/CustomDialog';
import Page from '../../../components/Page';
import ApiUrls from '../../../helper/ApiUrls';
import { API } from '../../../services/axios.service';
import { store } from '../../../store/Store';
import PromptBox from '../../chat-bots/inc/PromptBox';
import PlaygroundForm from './PlaygroundForm';

const Playground = () => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const [agent,setAgent] = useState(null);
    const [historyDel, setHistoryDel] = useState(false); // force re-render to promptbox for re-fetching history
    const params = useParams();
    const [topicToDelete, setTopicToDelete] = useState(null);

    useEffect(() => {
        dispatch(fetchLLMList());
    }, []);

    useEffect(() => {
        const selectedAgent = store.getState().selectedAgent.data;
        if (!selectedAgent){
            API.get(`/chatbot/details/${params.chatbotId}`).then(res => {
                console.log('data', res.data.data);
                setAgent(res.data.data);
                dispatch(setSelectedAgent(res.data.data));
            })
        } else {
            setAgent(selectedAgent);
        }
    }, []);

    const deleteTopic = (topic) => {
        API.delete(ApiUrls.DEL_CONVO(topic))
            .then(() => {
                enqueueSnackbar('Conversation cleared successfully! 🧹', { variant: 'success' });
            }).catch((err) => {
                console.error('Failed to delete conversations:', err);
                enqueueSnackbar(
                    err.response?.data?.message || 'Failed to delete conversations', 
                    { variant: 'error' }
                );
            })
            .finally(() => {
                setTopicToDelete(null);
                setHistoryDel(prevst => !prevst);
            });
    };

    return (
        <>
            <Page title="Playground">
                <Box sx={{ 
                    height: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    p: { xs: 1.5, sm: 2 },
                    maxWidth: '100%',
                    mx: 'auto'
                }}>
                    {/* Main Content Container */}
                    <Box sx={{ 
                        flex: 1, 
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <Grid container spacing={3} sx={{ flex: 1, minHeight: 0 }}>
                            {/* Configuration Panel */}
                            <Grid size={5} sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Fade in timeout={600}>
                                    <Card sx={{ 
                                        borderRadius: 2, 
                                        boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        height: {
                                            xs: 'auto',
                                            lg: 'calc(100vh - 120px)' // Adjust based on padding
                                        },
                                        maxHeight: {
                                            xs: '600px',
                                            lg: 'calc(100vh - 120px)'
                                        },
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}>
                                        <PlaygroundForm agent={agent}/>
                                    </Card>
                                </Fade>
                            </Grid>

                            {/* Chat Interface */}
                            <Grid size={7} sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Fade in timeout={800}>
                                    <Card sx={{ 
                                        borderRadius: 2, 
                                        boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        height: {
                                            xs: 'auto',
                                            lg: 'calc(100vh - 120px)' // Match the left panel
                                        },
                                        maxHeight: {
                                            xs: '600px',
                                            lg: 'calc(100vh - 120px)'
                                        },
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}>
                                        <CardContent sx={{ 
                                            p: 0, 
                                            flex: 1, 
                                            display: 'flex', 
                                            flexDirection: 'column',
                                            minHeight: 0,
                                            '&:last-child': { pb: 0 }
                                        }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                minHeight: 0,
                                                position: 'relative'
                                            }}>
                                                <PromptBox 
                                                    exclude="148px" 
                                                    historyDel={historyDel}
                                                    onClearConversation={() => setTopicToDelete(params.chatbotId)}
                                                />
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Fade>
                            </Grid>
                        </Grid>

                        {/* Helper Info
                        <Fade in timeout={1000}>
                            <Alert 
                                severity="info" 
                                sx={{ 
                                    mt: 3,
                                    borderRadius: 2,
                                    flexShrink: 0,
                                    '& .MuiAlert-message': {
                                        width: '100%'
                                    }
                                }}
                            >
                                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                                    💡 Playground Tips
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    • Modify bot settings on the left and test changes instantly
                                    • Use the chat interface to simulate real conversations
                                    • Clear conversation history to start fresh testing sessions
                                    • Changes here affect your live chatbot immediately
                                </Typography>
                            </Alert>
                        </Fade> */}
                    </Box>
                </Box>
            </Page>

            {/* Delete Confirmation Dialog */}
            <CustomDialog
                open={!!topicToDelete}
                title="Clear Conversation"
                contentText="Are you sure you want to clear this conversation? This action cannot be undone."
                onConfirm={() => {
                    deleteTopic(topicToDelete);
                }}
                onClose={() => {
                    setTopicToDelete(null);
                }}
                confirmText="Clear"
                cancelText="Cancel"
            />
        </>
    );
};

export default Playground;